import {
	Button,
	Grid,
	Input,
	List,
	Loader,
	Message,
	Modal,
	Popup,
} from '@mairistem/composants';
import { getErrorMessage, Icon, UserRole } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import PerimetreIdee from '../../../../../constants/simulation/perimetreIdee';
import type TreeRow from '../../../../../interfaces/feuilleSaisie/treeRow';
import type {
	default as IdeeImportable,
	ValeursPerimetresIdee,
} from '../../../../../interfaces/simulation/ideeImportable';
import { getIdeesImportables } from '../../../../../services/simulation/montantSimulation';
import {
	getImputationParams,
	getImputationText,
} from '../../../../../utils/imputation';
import {
	filtrerIdeesParLibelle,
	getIdeesDuPerimetre,
	getLibelleSelection,
} from '../../../../../utils/simulation/perimetreIdee';
import ImportIdeeItem from './ImportIdeeItem';
import ImportIdeePerimetre from './ImportIdeePerimetre';
import './ImportIdeeModal.css';

// Regles d'eligibilite rappelees dans l'infobulle de la phrase d'emphase : le
// filtrage lui-meme est fait par le back. Le budget et l'annee de
// programmation sont ceux de l'etape budgetaire en cours
const getReglesEligibilite = (
	saisieBudgetService: boolean,
	userRole: UserRole,
	libelleBudget?: string,
	anneeExercice?: number,
) => [
	'Statut prête',
	...(saisieBudgetService
		? [
				userRole < UserRole.CHARGE_PILOTAGE
					? "Rattachée à l'un de vos services"
					: 'Service habilité sur la ligne, ou sans service',
			]
		: []),
	...(libelleBudget ? [`Budget : ${libelleBudget}`] : []),
	...(anneeExercice ? [`Année de programmation : ${anneeExercice}`] : []),
	'Non liée à une APCP',
	'Non déjà importée',
];

interface ImportIdeeModalProps {
	anneeExercice?: number;
	identifiantSimulation: number;
	libelleBudget?: string;
	onClose: () => void;
	// Cree une demande par idee importee
	onImport: (idees: IdeeImportable[]) => Promise<void>;
	rowData: TreeRow;
	saisieBudgetService: boolean;
	userRole: UserRole;
}

const ImportIdeeModal = ({
	anneeExercice,
	identifiantSimulation,
	libelleBudget,
	onClose,
	onImport,
	rowData,
	saisieBudgetService,
	userRole,
}: ImportIdeeModalProps) => {
	const [loading, setLoading] = React.useState(true);
	const [idees, setIdees] = React.useState<IdeeImportable[]>([]);
	const [valeursPerimetres, setValeursPerimetres] =
		React.useState<ValeursPerimetresIdee>();
	// La modale est montee a chaque ouverture : on revient toujours sur le
	// premier cran
	const [perimetre, setPerimetre] = React.useState(PerimetreIdee.ARTICLE);
	const [recherche, setRecherche] = React.useState('');
	const [importEnCours, setImportEnCours] = React.useState(false);
	const [selection, setSelection] = React.useState<Set<number>>(new Set());

	// L'eligibilite (statut, service, lien, entite, annee) et le tri par
	// libelle sont appliques par le back, qui renvoie aussi le cran de chaque
	// idee : seul le filtrage selon le cran choisi est fait ici
	const fetchIdees = async () => {
		try {
			const { data, perimetres } = await getIdeesImportables(
				new URLSearchParams(
					getImputationParams(identifiantSimulation, rowData),
				),
			);
			setIdees(data);
			setValeursPerimetres(perimetres);
		} catch (e) {
			toast.error(getErrorMessage(e, 'Erreur lors du chargement des idées'));
		} finally {
			setLoading(false);
		}
	};

	// La recherche s'applique dans le cran actif et reste appliquee quand il
	// change
	const ideesDuPerimetre = getIdeesDuPerimetre(idees, perimetre);
	const ideesAffichees = filtrerIdeesParLibelle(ideesDuPerimetre, recherche);
	// Une idee cochee sortie de la liste visible n'est plus comptee ni importee,
	// mais reste cochee si elle redevient visible
	const ideesAImporter = ideesAffichees.filter((idee) =>
		selection.has(idee.identifiant),
	);

	// Le parent cree les demandes et ferme la sous-modale en cas de succes
	const importer = async () => {
		setImportEnCours(true);
		try {
			await onImport(ideesAImporter);
		} finally {
			setImportEnCours(false);
		}
	};
	const rechercheSansResultat =
		ideesDuPerimetre.length > 0 && ideesAffichees.length === 0;

	const toggleIdee = (identifiant: number) =>
		setSelection((precedente) => {
			const suivante = new Set(precedente);
			if (!suivante.delete(identifiant)) suivante.add(identifiant);
			return suivante;
		});

	return (
		<Modal
			closeIcon
			data-testid="importIdeeModal"
			onClose={onClose}
			onMount={fetchIdees}
			open
			size="small"
		>
			<Modal.Header content="Importer depuis Idéation · Vision 360" />
			{/* Le Modal de @mairistem/composants ne rend qu'un seul Modal.Content :
			phrase d'emphase, commande de perimetre, recherche et liste y
			cohabitent */}
			<Modal.Content scrolling={false}>
				<p data-testid="importIdeePhrase">
					Chaque idée sélectionnée crée une demande d'inscription budgétaire sur
					la ligne <strong>{getImputationText(rowData)}</strong>.{' '}
					<Popup
						content={
							<List
								bulleted
								data-testid="importIdeeRegles"
								items={getReglesEligibilite(
									saisieBudgetService,
									userRole,
									libelleBudget,
									anneeExercice,
								)}
							/>
						}
						header="Idées proposées"
						position="bottom right"
						trigger={
							<span data-testid="importIdeeReglesTrigger">
								<Icon cursor="help" iconSet="Lucide" name="Info" size={14} />
							</span>
						}
					/>
				</p>
				{valeursPerimetres && (
					<ImportIdeePerimetre
						idees={idees}
						onChange={setPerimetre}
						perimetre={perimetre}
						valeurs={valeursPerimetres}
					/>
				)}
				<Input
					className="importIdeeRecherche"
					fluid
					icon="search"
					iconPosition="left"
					input={{ 'data-testid': 'importIdeeRecherche' }}
					onChange={(_e, { value }) => setRecherche(value)}
					placeholder="Rechercher une idée par son libellé…"
					value={recherche}
				/>
				<div className="importIdeeModalListe">
					{loading ? (
						<Loader active inline="centered" />
					) : rechercheSansResultat ? (
						<Message
							content="Modifiez ou effacez votre recherche."
							data-testid="importIdeeVide"
							header="Aucune idée ne correspond à la recherche"
						/>
					) : ideesAffichees.length === 0 ? (
						<Message
							content="Élargissez le périmètre pour voir davantage d'idées."
							data-testid="importIdeeVide"
							header="Aucune idée sur ce périmètre"
						/>
					) : (
						ideesAffichees.map((idee) => (
							<ImportIdeeItem
								idee={idee}
								key={idee.identifiant}
								onToggle={() => toggleIdee(idee.identifiant)}
								perimetre={perimetre}
								saisieBudgetService={saisieBudgetService}
								selected={selection.has(idee.identifiant)}
							/>
						))
					)}
				</div>
			</Modal.Content>
			<Modal.Actions>
				<Grid columns={2} verticalAlign="middle">
					<Grid.Column data-testid="importIdeeSelection" textAlign="left">
						{getLibelleSelection(ideesAImporter.length)}
					</Grid.Column>
					<Grid.Column textAlign="right">
						{/* Annuler, la croix, un clic hors de la modale ou Echap
						abandonnent la selection : la modale est demontee */}
						<Button basic content="Annuler" onClick={onClose} />
						{/* Seules les idees affichees et cochees sont importees */}
						<Button
							content="Importer la sélection"
							data-testid="importIdeeImporter"
							disabled={ideesAImporter.length === 0 || importEnCours}
							loading={importEnCours}
							onClick={importer}
							primary
						/>
					</Grid.Column>
				</Grid>
			</Modal.Actions>
		</Modal>
	);
};

export default ImportIdeeModal;
